import React, { useEffect, useState } from "react";
import { selectUser } from "../../../features/userSlice";
import { useSelector } from "react-redux";
import {
  TextField,
  Dialog,
  Tooltip,
} from "@mui/material";
import DatePicker from "react-datepicker";
import {
  applied_fields,
  backset_fields,
  bolt_type_fields,
  casing_type_fields,
  catch_astragal,
  core_fields,
  hinge_finish_fields,
  size_jamb_type_fields,
  stop_applied_fields,
  stop_fields,
} from "../../../components/Forms/SelectValues/values";
import {
  formAutoSave,
  hasDraftContent,
} from "../../../components/Forms/Misc/utilities";
import { Link, useNavigate } from "react-router-dom";
import DoorBuilder from "../../../components/WindowDoor/DoorBuilder";
import DisplayDoors from "../../../components/Forms/InteriorDoor/DisplayDoors";
import DoorBuilderEdit from "../../../components/WindowDoor/Components/DoorBuilderEdit";
import { getSteps } from "../../../components/WindowDoor/utilities";
import { getWindowDoorUsers } from "../../Admin/Utilities/api";
import StockItems from "../../../components/WindowDoor/Components/StockItems";
import DoorFrontIcon from "@mui/icons-material/DoorFront";
import { IconTile } from "../../../components/UI/Page";

const InteriorDoor = (props) => {
  //This will have data or be null either is fine.
  const [formData, setFormData] = useState(props.formData);
  const [formID, setFormID] = useState(null);
  const [submitStatus, setStatus] = useState(false);
  const [preSubmit, setPreSubmit] = useState(false);
  const [sendTo, setSendTo] = useState(null);
  const user = useSelector(selectUser);
  const navigate = useNavigate();

  //Using existing door data if it exists, if not use a blank row.
  const [doors, setDoors] = useState(props.formData.doorData || []);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editRowData, setEditRowData] = useState(null);

  const handleEditRow = (rowData) => {
    setEditRowData(rowData);
    setIsEditDialogOpen(true); // Open the dialog for editing
  };

  //Update formdata with new door data
  useEffect(() => {
    setFormData({ ...formData, doorData: doors });
  }, [doors]);

  // Auto Save Function Run Every time Form Data Changes or Doors Change.
  // Skipped until something is actually entered so opening the page doesn't
  // create an empty draft.
  useEffect(() => {
    if (formData && hasDraftContent(formData)) {
      formAutoSave(formData, setFormID);
    }
  }, [formData]);

  useEffect(() => {
    if (formData.door_type === "single") {
      setFormData({
        ...formData,
        casing_type: null,
        hinge_finish: null,
        stop: null,
        stop_applied: null,
        applied: null,
        handing: null,
        bolt_type: null,
        backset: null,
        core: null,
      });
    }
    if (formData.door_type === "double") {
      setFormData({
        ...formData,
        casing_type: null,
        hinge_finish: null,
        stop: null,
        stop_applied: null,
        applied: null,
        handing: null,
        bolt_type: "N/A",
        backset: "N/A",
        core: null,
      });
    }
    if (formData.door_type === "bifold") {
      setFormData({
        ...formData,
        casing_type: null,
        hinge_finish: null,
        stop: null,
        stop_applied: null,
        applied: null,
        core: "Bifold",
        bolt_type: null,
        backset: null,
        handing: "Bifold",
        size_jamb_type: null,
      });
    }
  }, [formData.door_type]);

  useEffect(() => {
    if (!formData.size_jamb_type) return;
    if (formData.size_jamb_type.includes("SPLIT")) {
      setFormData((prev) => ({
        ...prev,
        hinge_finish: "",
        stop: "N/A",
        stop_applied: "N/A",
      }));
    } else if (formData.size_jamb_type.includes("Door Slab Only")) {
      setFormData((prev) => ({
        ...prev,
        slab_only: 1,
        casing_type: "N/A",
        hinge_finish: "N/A",
        stop: "N/A",
        stop_applied: "N/A",
        applied: "N/A",
        handing: "N/A",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        slab_only: 0,
        hinge_finish: "",
        stop: "",
        stop_applied: "",
      }));
    }
  }, [formData.size_jamb_type]);

  useEffect(() => {
    if (sendTo) {
      handleSubmit();
      setSendTo(null);
    }
  }, [sendTo]);

  const handleSaveSpecialInstructions = (rowData, specialInstructions) => {
    const updatedDoors = doors.map((door) =>
      door === rowData
        ? { ...door, special_instructions: specialInstructions }
        : door
    );
    setDoors(updatedDoors);
  };

  const handleSubmit = async () => {
    //Once the form is submitted, stop the auto save.
    var data = formData;

    data.entry_date = new Date();
    data.submit_user = user;
    data.form_type = "order";
    data.submitted = 1;
    data.sendTo = sendTo;

    submitForm(data);
  };

  const submitForm = async (data) => {
    const rawResponse = await fetch("/api/forms/submit", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    const content = await rawResponse.json();

    if (content.success === 1) {
      setStatus(true);
      await setFormID(content.form_id);
      return;
    } else {
      setStatus(false);
      return;
    }
  };

  //Get Valid Date for Date Picker
  const validDate = () => {
    if (formData.shipping_date) {
      return new Date(formData.shipping_date);
    } else {
      setFormData({ ...formData, shipping_date: new Date() });
      return new Date();
    }
  };

  const handleSaveRowData = (rowData) => {
    const rowIndex = doors.findIndex((door) => door === editRowData);
    if (rowIndex !== -1) {
      const updatedDoors = [...doors];
      updatedDoors[rowIndex] = rowData;
      setDoors(updatedDoors);
    }

    setEditRowData(null);
    setIsEditDialogOpen(false);
  };

  const [saveSuccessDialogOpen, setSaveSuccessDialogOpen] = useState(false);

  const handleSave = async (openDialog) => {
    var data = formData;

    data.entry_date = new Date();
    data.submit_user = user;
    data.form_type = formData.form_type.toUpperCase();

    // Simply save the data without submitting
    await formAutoSave(formData, setFormID);

    if (openDialog) {
      setSaveSuccessDialogOpen(true); // Open the dialog for save success
    }
  };

  const handleCloseSaveSuccessDialog = () => {
    setSaveSuccessDialogOpen(false); // Close the dialog for save success
    const currentRoute = window.location.pathname;

    if (currentRoute !== "/forms/viewall") {
      navigate("/forms/viewall"); // Navigate to "/forms/viewall" if not already on that page
    } else {
      window.location.reload(); // Refresh the page if already on "/forms/viewall"
    }
  };

  const handlePrint = async () => {
    // Save the data
    handleSave(false);
    window.open(`/api/forms/pdf?id=${formID}`, "_blank");
  };

  // Reusable select class
  const selectCls =
    "app-input w-full rounded-xl border px-3 py-2.5 text-sm transition-colors";

  const doorCost = doors.reduce(
    (total, door) => total + (door.estimated_price || 0),
    0
  );
  const extraCost = formData.totalExtraStockCost || 0;
  const totalCost = doorCost + extraCost;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 text-[color:var(--app-text)] sm:px-6">
      {/* ── Dialogs (unchanged logic) ── */}
      <Dialog
        open={submitStatus}
        onClose={() => {
          setStatus(false);
          navigate("/forms/viewall");
        }}
        maxWidth="sm"
        fullWidth
      >
        <div className="p-6 text-center sm:p-8">
          <div className="app-icon-tile mx-auto mb-4 h-14 w-14 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-xl font-semibold mb-1">
            {formData.form_type.toUpperCase()} Submitted
          </p>
          <p className="app-muted mb-6 text-sm">Notification has been sent!</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to={`/api/forms/pdf?id=${formID}`}
              target="_blank"
              className="app-btn app-btn-secondary"
            >
              Download PDF
            </Link>
            <button
              type="button"
              className="app-btn app-btn-primary"
              onClick={() => {
                setStatus(false);
                const currentRoute = window.location.pathname;
                if (currentRoute !== "/forms/viewall") {
                  navigate("/forms/viewall");
                } else {
                  window.location.reload();
                }
              }}
            >
              Close
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={preSubmit}
        onClose={() => setPreSubmit(false)}
        maxWidth="sm"
        fullWidth
      >
        <div className="relative p-6">
          <button
            type="button"
            aria-label="Close"
            onClick={() => setPreSubmit(false)}
            className="app-icon-btn absolute right-3 top-3"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <p className="text-lg font-semibold text-center">Send To</p>
          <p className="app-muted mb-5 mt-1 text-center text-sm">
            Who should receive this order?
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              className="app-btn app-btn-secondary app-btn-lg flex-1 sm:max-w-[220px]"
              onClick={() => {
                setSendTo("tiffiney@bigclumber.com");
                setPreSubmit(false);
              }}
            >
              Tiffiney Sweezey
            </button>
            <button
              type="button"
              className="app-btn app-btn-secondary app-btn-lg flex-1 sm:max-w-[220px]"
              onClick={() => {
                setSendTo("rong@bigclumber.com");
                setPreSubmit(false);
              }}
            >
              Ron Goetz
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={saveSuccessDialogOpen}
        onClose={handleCloseSaveSuccessDialog}
        maxWidth="xs"
        fullWidth
      >
        <div className="p-8 text-center">
          <div className="app-icon-tile mx-auto mb-4 h-14 w-14 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-lg font-semibold mb-1">Saved!</p>
          <p className="app-muted mb-6 text-sm">Your form has been saved.</p>
          <button
            type="button"
            onClick={handleCloseSaveSuccessDialog}
            className="app-btn app-btn-primary px-8"
          >
            OK
          </button>
        </div>
      </Dialog>

      {/* ── Header ── */}
      <div className="app-card mb-5 overflow-hidden rounded-2xl border">
        <div className="app-divider flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <IconTile icon={DoorFrontIcon} tone="sky" />
            <div className="min-w-0">
              <p className="app-eyebrow">Forms</p>
              <h1 className="text-xl font-bold tracking-tight">
                Interior Door {formData.form_type?.toUpperCase() || "QUOTE"}
              </h1>
              <p className="app-muted mt-0.5 truncate text-sm">
                {formData.customer || "No Customer"} {formData.job ? `| Job #${formData.job}` : ""}
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
            Auto-saving
          </span>
        </div>

        {/* ── Section: Order Details ── */}
        <div className="px-5 py-5 sm:px-6">
          <h2 className="app-eyebrow mb-3">
            Order Details
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium">
                Shipping Date
              </label>
              <DatePicker
                selected={formData.shipping_date ? validDate() : null}
                minDate={new Date()}
                onChange={(date) =>
                  setFormData({ ...formData, shipping_date: date })
                }
                className={selectCls}
                placeholderText="Select date..."
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium">
                Special Instructions
              </label>
              <TextField
                onChange={(e) =>
                  setFormData({ ...formData, instructions: e.target.value })
                }
                className="w-full"
                size="small"
                multiline
                rows={2}
                value={formData.instructions || ""}
                placeholder="Any special notes..."
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">
                Ft. Wayne Prep
              </label>
              <select
                className={selectCls}
                value={formData.ftwayne_prep ? "true" : "false"}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    ftwayne_prep: e.target.value === "true",
                  })
                }
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
          </div>
        </div>

        <hr className="app-divider border-t" />

        {/* ── Section: Door Configuration ── */}
        <div className="px-5 py-5 sm:px-6">
          <h2 className="app-eyebrow mb-3">
            Door Configuration
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Door Type */}
            <div>
              <label className="mb-1 block text-sm font-medium">
                Door Type
              </label>
              <select
                className={selectCls}
                value={formData.door_type || ""}
                onChange={(e) => {
                  switch (e.target.value) {
                    case "double":
                      setFormData({ ...formData, door_type: e.target.value });
                      break;
                    case "bifold":
                      setFormData({
                        ...formData,
                        door_type: e.target.value,
                        handing: "Bifold",
                        stop: "N/A",
                        stop_applied: "N/A",
                        hinge_finish: "N/A",
                      });
                      break;
                    default:
                      setFormData({
                        ...formData,
                        door_type: e.target.value,
                        catch_astragal: null,
                      });
                      break;
                  }
                }}
              >
                <option key={0} value="single">Single</option>
                <option key={1} value="double">Double</option>
                <option key={2} value="bifold">Bifold</option>
              </select>
            </div>

            {/* Ball Catch / Astragal (double only) */}
            {formData.door_type === "double" && (
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Ball Catch | Astragal
                </label>
                <select
                  className={selectCls}
                  value={formData.catch_astragal || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, catch_astragal: e.target.value })
                  }
                >
                  {catch_astragal.map((item, index) => (
                    <option key={index} value={`${item.description}(${item.productId})`}>
                      {index === 0 ? item.description : `${index}) ${item.description}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Jamb Size and Type (non-bifold) */}
            {formData.door_type !== "bifold" && (
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Jamb Size & Type
                </label>
                <select
                  className={selectCls}
                  value={formData.size_jamb_type || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, size_jamb_type: e.target.value })
                  }
                >
                  {size_jamb_type_fields.map((item, index) => {
                    const optionValue =
                      item.description === ""
                        ? ""
                        : `${item.description}(${item.productId})`;
                    return (
                      <option key={index} value={optionValue}>
                        {index === 0
                          ? item.description || "Select Jamb Type..."
                          : `${index}) ${item.description}`}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {/* Core (always shown for non-bifold) */}
            {formData.door_type !== "bifold" && (
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Core
                </label>
                <select
                  className={selectCls}
                  value={formData.core || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, core: e.target.value })
                  }
                >
                  {core_fields.map((item, index) => (
                    <option key={index} value={item}>
                      {index === 0 ? "Select Core..." : `${index}) ${item}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* ── Section: Hardware (non-bifold, non-slab-only) ── */}
        {formData.door_type !== "bifold" &&
          formData.size_jamb_type !== "Door Slab Only(null)" && (
            <>
              <hr className="app-divider border-t" />
              <div className="px-5 py-5 sm:px-6">
                <h2 className="app-eyebrow mb-3">
                  Jamb Hardware
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {/* Casing Type */}
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      Casing Type
                    </label>
                    <select
                      className={selectCls}
                      value={formData.casing_type || ""}
                      onChange={(e) => {
                        const isNA = e.target.value === "N/A(null)";
                        setFormData({
                          ...formData,
                          casing_type: e.target.value,
                          ...(isNA ? { applied: "No" } : {}),
                        });
                      }}
                    >
                      {casing_type_fields.map((item, index) => (
                        <option key={index} value={`${item.description}(${item.productId})`}>
                          {index === 0 ? item.description || "Select Casing..." : `${index}) ${item.description}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Casing Applied */}
                  {formData.casing_type !== "N/A(null)" && (
                    <div>
                      <label className="mb-1 block text-sm font-medium">
                        Casing Applied
                      </label>
                      <select
                        className={selectCls}
                        value={formData.applied || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, applied: e.target.value })
                        }
                      >
                        {applied_fields.map((item, index) => (
                          <option key={index} value={item}>
                            {index === 0 ? "Select..." : `${index}) ${item}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Hinge Finish */}
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      Hinge Finish
                    </label>
                    <select
                      className={selectCls}
                      value={formData.hinge_finish || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, hinge_finish: e.target.value })
                      }
                    >
                      {hinge_finish_fields.map((item, index) => (
                        <option key={index} value={`${item.description}(${item.productId})`}>
                          {index === 0 ? item.description || "Select Finish..." : `${index}) ${item.description}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Stop */}
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      Stop
                    </label>
                    <select
                      className={selectCls}
                      value={formData.stop || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, stop: e.target.value })
                      }
                    >
                      {stop_fields(formData.size_jamb_type).map((item, index) => (
                        <option key={index} value={`${item.description}(${item.productId})`}>
                          {index === 0 ? item.description || "Select Stop..." : `${index}) ${item.description}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Stop Applied */}
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      Stop Applied
                    </label>
                    <select
                      className={selectCls}
                      value={formData.stop_applied || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, stop_applied: e.target.value })
                      }
                    >
                      {stop_applied_fields(formData.size_jamb_type).map(
                        (item, index) => (
                          <option key={index} value={item}>
                            {index === 0 ? "Select..." : `${index}) ${item}`}
                          </option>
                        )
                      )}
                    </select>
                  </div>

                  {/* Bolt Type */}
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      Bolt Type
                    </label>
                    <select
                      className={selectCls}
                      value={formData.bolt_type || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, bolt_type: e.target.value })
                      }
                    >
                      {bolt_type_fields(
                        formData.size_jamb_type,
                        formData.door_type
                      ).map((item, index) => (
                        <option key={index} value={item}>
                          {index === 0 ? item : `${index}) ${item}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Backset */}
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      Backset
                    </label>
                    <select
                      className={selectCls}
                      value={formData.backset || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, backset: e.target.value })
                      }
                    >
                      {backset_fields(
                        formData.size_jamb_type,
                        formData.door_type
                      ).map((item, index) => (
                        <option key={index} value={item}>
                          {index === 0 ? item : `${index}) ${item}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}

        {/* ── Section: Slab-Only Hardware (bolt + backset only) ── */}
        {formData.door_type !== "bifold" &&
          formData.size_jamb_type === "Door Slab Only(null)" && (
            <>
              <hr className="app-divider border-t" />
              <div className="px-5 py-5 sm:px-6">
                <h2 className="app-eyebrow mb-3">
                  Slab Hardware
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      Bolt Type
                    </label>
                    <select
                      className={selectCls}
                      value={formData.bolt_type || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, bolt_type: e.target.value })
                      }
                    >
                      {bolt_type_fields(
                        formData.size_jamb_type,
                        formData.door_type
                      ).map((item, index) => (
                        <option key={index} value={item}>
                          {index === 0 ? item : `${index}) ${item}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      Backset
                    </label>
                    <select
                      className={selectCls}
                      value={formData.backset || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, backset: e.target.value })
                      }
                    >
                      {backset_fields(
                        formData.size_jamb_type,
                        formData.door_type
                      ).map((item, index) => (
                        <option key={index} value={item}>
                          {index === 0 ? item : `${index}) ${item}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
      </div>

      {/* ── Door Builder ── */}
      <DoorBuilder
        doors={doors}
        setDoors={setDoors}
        formData={formData}
        setFormData={setFormData}
      />

      {editRowData && (
        <DoorBuilderEdit
          steps={getSteps(formData?.size_jamb_type)}
          rowData={editRowData}
          setRowData={setEditRowData}
          setIsEditDialogOpen={setIsEditDialogOpen}
          isEditDialogOpen={isEditDialogOpen}
          onSaveRowData={handleSaveRowData}
          onSaveSpecialInstructions={handleSaveSpecialInstructions}
        />
      )}

      {doors.length > 0 && (
        <DisplayDoors
          doors={doors}
          formData={formData}
          setDoors={setDoors}
          onEditRow={handleEditRow}
          setEditRowData={setEditRowData}
          onSaveSpecialInstructions={handleSaveSpecialInstructions}
        />
      )}

      <StockItems formData={formData} setFormData={setFormData} />

      {/* ── Pricing Summary & Actions ── */}
      {(doors.length > 0 || formData.extraStockItems?.length > 0) && (
        <div className="app-card mt-5 overflow-hidden rounded-2xl border">
          <div className="px-5 py-5 sm:px-6">
            <h2 className="app-eyebrow mb-4">
              Pricing Summary
            </h2>
            <div className="mx-auto grid max-w-lg grid-cols-3 gap-2 text-center sm:gap-4">
              <div className="app-surface rounded-xl border p-3">
                <p className="app-muted mb-1 text-xs">Door Cost</p>
                <p className="text-base font-bold tabular-nums text-emerald-600 dark:text-emerald-300 sm:text-lg">${doorCost.toFixed(2)}</p>
              </div>
              <div className="app-surface rounded-xl border p-3">
                <p className="app-muted mb-1 text-xs">Extra Stock</p>
                <p className="text-base font-bold tabular-nums text-sky-600 dark:text-sky-300 sm:text-lg">${extraCost.toFixed(2)}</p>
              </div>
              <div className="rounded-xl border border-sky-500/40 bg-sky-500/10 p-3">
                <p className="app-muted mb-1 text-xs">Total</p>
                <p className="text-lg font-extrabold tabular-nums text-sky-700 dark:text-sky-300 sm:text-xl">${totalCost.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="app-surface flex flex-wrap justify-center gap-3 border-t px-5 py-4 sm:justify-end sm:px-6">
            <Tooltip title="Save Form">
              <button
                type="button"
                onClick={() => handleSave(true)}
                className="app-btn app-btn-secondary"
              >
                <svg className="w-4 h-4" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Save & Close
              </button>
            </Tooltip>
            {formID && (
              <Tooltip title="Print PDF">
                <button
                  type="button"
                  onClick={handlePrint}
                  className="app-btn app-btn-secondary"
                >
                  <svg className="w-4 h-4" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print PDF
                </button>
              </Tooltip>
            )}
            <Tooltip title="Submit Form as Order">
              <button
                type="button"
                onClick={() => setPreSubmit(true)}
                className="app-btn app-btn-primary"
              >
                <svg className="w-4 h-4" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Submit as Order
              </button>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteriorDoor;
